import "./LoginForm.style.css";

import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { ApiFactory } from "../../../api-client/api-factory";
import { LoginRequest, UsersApi } from "../../../api-client";
import { AuthorizationStatus } from "../../../utils/enums/AuthorizationStatus";
import { useNavigate } from "react-router-dom";
import { AppRouter } from "../../../utils/enums/AppRouter";
import { useAppDispatch } from "../../../store/store";
import { requireAuthorization } from "../../../store/actions/appActions";
import { validateEmail } from "../../../utils/functions/validation/validate-email";
import { validatePassword } from "../../../utils/functions/validation/validate-password";
import {
  Button,
  ButtonColor,
  ButtonType,
} from "../../../components/base/Button/Button";

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginPageFormProps {
  setUsername: (newUsername: string) => void;
}

type ValidationErrors = {
  email: string | null;
  password: string | null;
};

export function LoginForm({ setUsername }: LoginPageFormProps) {
  const dispatch = useAppDispatch();

  const [loginFormData, setLoginFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const navigate = useNavigate();

  const [errors, setErrors] = useState<ValidationErrors>({
    email: null,
    password: null,
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  const calculateErrors = (data: LoginFormData): ValidationErrors => {
    const newErrors: ValidationErrors = {
      email: null,
      password: null,
    };

    if (!validateEmail(data.email)) {
      newErrors.email = "Некорректный email";
    }

    if (!validatePassword(data.password)) {
      newErrors.password = "Неправильные данные";
    }

    return newErrors;
  };

  const validateForm = (): boolean => {
    const noErrors = !errors.email && !errors.password;
    const allFieldsFilled =
      loginFormData.email.length > 0 && loginFormData.password.length > 0;
    return noErrors && allFieldsFilled;
  };

  useEffect(() => {
    if (formSubmitted) {
      const calculatedErrors = calculateErrors(loginFormData);
      setErrors(calculatedErrors);
    }
  }, [loginFormData, formSubmitted]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setLoginFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (formSubmitted) {
      validateForm();
    }
  };

  const handleSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitted(true);

    const currentErrors = calculateErrors(loginFormData);
    setErrors(currentErrors);

    if (
      Object.values(currentErrors).every((err) => err === null) &&
      loginFormData.email.length > 0 &&
      loginFormData.password.length > 0
    ) {
      const request: LoginRequest = {
        email: loginFormData.email,
        password: loginFormData.password,
      };

      ApiFactory.get(UsersApi)
        .loginPost(request)
        .then((data) => {
          if (data.user?.name) {
            setUsername(data.user.name);
          }
          dispatch(requireAuthorization(AuthorizationStatus.Auth));
          navigate(AppRouter.Main);
        })
        .catch(() => {
          console.error("Не получилось зайти в аккаунт");
          setErrors((prevState) => {
            return {
              email: prevState.email,
              password: "неправильные данные",
            };
          });
          dispatch(requireAuthorization(AuthorizationStatus.NoAuth));
        });
    }
  };

  return (
    <form onSubmit={handleSubmitForm} className="login-form">
      <div className="login-form__blocks">
        <div className="login-form__block header_4">
          <div className="login-form__input header_4">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              onChange={handleChange}
              className={`login-form__email_input text_2 ${errors.email ? "error" : ""}`}
              value={loginFormData.email}
              aria-invalid={!!errors.email}
              aria-describedby="email-error"
            />
          </div>
          {formSubmitted && errors.email && (
            <p id="email-error" className="error-message text_2">
              {errors.email}
            </p>
          )}
        </div>

        <div className="login-form__block header_4">
          <div className="login-form__input header_4">
            <label htmlFor="password">Пароль</label>
            <input
              type="password"
              id="password"
              name="password"
              onChange={handleChange}
              className={`login-form__password_input text_2 ${errors.password ? "error" : ""}`}
              value={loginFormData.password}
              aria-invalid={!!errors.password}
              aria-describedby="password-error"
            />
          </div>
          {formSubmitted && errors.password && (
            <p id="password-error" className="error-message text_2">
              {errors.password}
            </p>
          )}
        </div>
      </div>
      <Button
        text="Войти"
        type={ButtonType.Submit}
        color={ButtonColor.Secondary}
        disabled={!validateForm()}
      />
    </form>
  );
}
